import { ComponentRef } from '@angular/core';

import { QuestionFormService } from '@modules/management/pages/details/check/services/question/question-form.service';
import { AbstractFieldsModel } from '@modules/management/pages/details/check/models/abstractFieldsModel';
import { QuestionFormGroupListComponent } from '@modules/management/pages/details/check/components';
import { FormBuilderService } from '@services';

import { TranslateService } from '@ngx-translate/core';
import { find, first, get, includes, values } from 'lodash';
import { QuestionType } from '@modules/management/pages/details/check/services/question/model';

export class IncrementEntryFieldsModel extends AbstractFieldsModel {

  public static UNITS = [
    {id: 'inches', description: 'MGMT_DETAILS.Inches'},
    {id: 'feet', description: 'MGMT_DETAILS.Feet'},
    {id: 'mm', description: 'MGMT_DETAILS.MM'},
    {id: 'cm', description: 'MGMT_DETAILS.CM'},
    {id: 'meters', description: 'MGMT_DETAILS.Meters'},
    // { id: 'ounces', description: 'MGMT_DETAILS.Ounces'},
    // { id: 'pounds', description: 'MGMT_DETAILS.Pounds'},
    {id: 'grams', description: 'MGMT_DETAILS.Grams'},
    {id: 'kilograms', description: 'MGMT_DETAILS.Kilograms'},
    // { id: 'fluidOunces', description: 'MGMT_DETAILS.FluidOunces'},
    // { id: 'gallons', description: 'MGMT_DETAILS.Gallons'},
    {id: 'milliliters', description: 'MGMT_DETAILS.Milliliters'},
    {id: 'liters', description: 'MGMT_DETAILS.Liters'},
    {id: 'degrees_c', description: 'MGMT_DETAILS.Degrees_C'},
    {id: 'degrees_f', description: 'MGMT_DETAILS.Degrees_F'}
  ];

  protected formConfig: any = {
    containers: true,
    prefix: 'incrementEntry',
    fields: [
      {
        containerClass: 'question-field',
        name: 'units',
        title: 'MGMT_DETAILS.Units',
        type: 'selectmenu',
        multiple: false,
        searchable: false,
        canClear: false,
        originalOrder: true,
        default: 'kilograms',
        options: IncrementEntryFieldsModel.UNITS,
        onChange: (value: any, $form: JQuery) => {
          if (includes(['inches', 'feet'], value)) {
            this.updateFractionIncrementFieldByType(value);
          } else {
            this.syncStartValueFieldStep(false);
          }

          this.markFieldAsChanged($form.attr('id'), 'units', value);
        },
        hideFieldsOnValue: {
          select: [
            {
              hideClasses: ['fraction-increment'],
              valueFunction: (val) => !includes(['inches', 'feet'], val)
            },
            {
              hideClasses: ['increment'],
              valueFunction: (val) => includes(['inches', 'feet'], val)
            }
          ]
        }
      },
      {
        containerClass: 'question-field',
        inputtype: 'verbatim',
        name: 'startValue',
        title: 'MGMT_DETAILS.Start_Value',
        type: 'number',
        size: 20,
        required: true
      },
      {
        containerClass: 'question-field increment',
        inputtype: 'verbatim',
        name: 'increment',
        title: 'MGMT_DETAILS.Increment',
        type: 'number',
        min: 0,
        size: 20,
        required: true
      },
      {
        containerClass: 'question-field fraction-increment',
        name: 'fractionIncrement',
        title: 'MGMT_DETAILS.Increment',
        type: 'selectmenu',
        multiple: false,
        searchable: false,
        canClear: false,
        originalOrder: true,
        options: [],
        required: true,
        onChange: (value: string) => this.applyFractionIncrementChanges(value)
      }
    ]
  };
  private fields = [
    {
      containerClass: 'question-type-field incrementEntry',
      title: 'MGMT_DETAILS.Increment_Entry_Details',
      type: 'subtitle'
    },
    {
      containerClass: 'question-type-field incrementEntry',
      title: 'MGMT_DETAILS.Increment_Values',
      type: 'subtitle'
    },
    {
      type: 'subtitle',
      containerClass: 'question-type-field incrementEntry form-group-title increment-entry-block'
    }
  ];

  constructor(
    protected questionFormService: QuestionFormService,
    private translate: TranslateService,
    private formBuilderService: FormBuilderService
  ) {
    super(questionFormService);
  }

  public getFields(): any[] {
    return this.fields;
  }

  public getData() {
    const settingsFields: string[] = [
      'units',
      'startValue',
      'increment',
      'fractionIncrement'
    ];

    return this.getFormData(settingsFields);
  }

  public showGroupList() {
    if (this.questionFormService.isModelAvailable(QuestionType.IncrementEntry)) {
      const groupElement = this.questionFormService.getElementBy('.increment-entry-block');

      if (groupElement && !this.formGroupComponentRef) {
        const settings = get(first(this.questionFormService.questionPage.questionOptions), 'settings') || {};
        if (includes(['inches', 'feet'], settings.units)) {
          setTimeout(() => {
            this.updateFractionIncrementFieldByType(settings.units, settings.fractionIncrement);
          }, 500);
        }

        this.questionFormService.dynamicComponentCreatorService.create(
          QuestionFormGroupListComponent,
          (component, componentRef) => this.onFormGroupComponentInit(component, componentRef),
          groupElement
        );
      } else {
        const unitsField = find(this.formConfig.fields, {name: 'units'});
        this.formBuilderService.hideFieldsOnValue(unitsField, this.formConfig, {});
      }
    }
  }

  public onDestroy() {
    this.formGroupComponentRef && this.formGroupComponentRef.destroy();
    this.formGroupComponentRef = null;
  }

  private onFormGroupComponentInit(component: QuestionFormGroupListComponent, componentRef: ComponentRef<QuestionFormGroupListComponent>) {
    this.onFormGroupComponentCreate(component, componentRef);

    const option = first(values(this.questionFormService.questionPage.questionOptions)) || {};
    this.formGroupComponent.disableNewGroup = true;

    this.formGroupComponent.groups = [{
      formConfig: this.formConfig,
      formData: option
    }];
  }

  private updateFractionIncrementFieldByType(type: 'inches' | 'feet', data?): void {
    const labelMap = {
      feet: 'foot',
      inches: 'inch'
    };

    const id = `#${this.formConfig.prefix}fractionIncrement`;
    const value = data || $(id).val();
    const fieldOptions: any = Object.assign(find(this.formConfig.fields, {name: 'fractionIncrement'}), {
      options: [
        {id: '1/64', description: '1/64'},
        {id: '1/32', description: '1/32'},
        {id: '1/16', description: '1/16'},
        {id: '1/8', description: '1/8'},
        {id: '1/4', description: '1/4'},
        {id: '1/2', description: '1/2'},
        {id: '1', description: `1 ${labelMap[type]}`}
      ]
    });
    this.formBuilderService.replaceSelectOptionsWith(id, this.formConfig, fieldOptions, {fractionIncrement: value});
    this.applyFractionIncrementChanges(value);
    this.syncStartValueFieldStep();
  }

  private applyFractionIncrementChanges(value: string | number) {
    value = isFinite(+value) ? +value : value;
    this.markFieldAsChanged(`${this.formConfig.prefix}Form`, 'fractionIncrement', value);
  }

  private syncStartValueFieldStep(enable: boolean = true) {
    const selector = `#${this.formConfig.prefix}startValue`;
    const element = this.formGroupComponentRef.location.nativeElement.querySelector(selector);

    if (element) {
      element.setAttribute('step', enable ? 1 : null);
    }
  }
}
